import React from 'react';
import PropTypes from 'prop-types';
import { useLocation } from 'react-router-dom';
import { Button, Dimmer } from 'semantic-ui-react';
import { readAsDataURL } from 'promise-file-reader';
import { useIntl, defineMessages } from 'react-intl';
import { toast } from 'react-toastify';
import loadable from '@loadable/component';

import deleteSVG from '@plone/volto/icons/delete.svg';
import navTreeSVG from '@plone/volto/icons/nav.svg';
import Icon from '@plone/volto/components/theme/Icon/Icon';
import Toast from '@plone/volto/components/manage/Toast/Toast';
import UniversalLink from '@plone/volto/components/manage/UniversalLink/UniversalLink';
import FormFieldWrapper from '@plone/volto/components/manage/Widgets/FormFieldWrapper';
import Image from '@plone/volto/components/theme/Image/Image';
import withObjectBrowser from '@plone/volto/components/manage/Sidebar/ObjectBrowser';
import { flattenToAppURL } from '@plone/volto/helpers/Url/Url';
import { validateFileUploadSize } from '@plone/volto/helpers/FormValidation/FormValidation';

const Dropzone = loadable(() => import('react-dropzone'));

const imageMimetypes = [
  'image/png',
  'image/jpeg',
  'image/webp',
  'image/jpg',
  'image/gif',
  'image/svg+xml',
];

const messages = defineMessages({
  releaseDrag: {
    id: 'Drop files here ...',
    defaultMessage: 'Drop files here ...',
  },
  editFile: {
    id: 'Drop file here to replace the existing file',
    defaultMessage:
      'File upload area. Press Enter or click to replace the existing file.',
  },
  fileDrag: {
    id: 'Drop file here to upload a new file',
    defaultMessage:
      'File upload area. Press Enter or click to open the file browser.',
  },
  replaceFile: {
    id: 'Replace existing file',
    defaultMessage: 'Replace existing file',
  },
  addNewFile: {
    id: 'Choose a file',
    defaultMessage: 'Choose a file',
  },
  pickAnImage: {
    id: 'pickAnImage',
    defaultMessage: 'Pick an existing image',
  },
  maxSizeError: {
    id: 'The file you uploaded exceeded the maximum allowed size of {size} bytes',
    defaultMessage:
      'The file you uploaded exceeded the maximum allowed size of {size} bytes',
  },
  acceptError: {
    id: 'File is not of the accepted type {accept}',
    defaultMessage: 'File is not of the accepted type {accept}',
  },
  requiredField: {
    id: 'This field is required.',
    defaultMessage: 'This field is required.',
  },
  downloadFile: {
    id: 'Download {filename}',
    defaultMessage: 'Download {filename}',
  },
  pickImageErrorMessage: {
    id: 'pickImageErrorMessage',
    defaultMessage: 'Could not load the selected image.',
  },
});

/**
 * Widget for real NamedBlobImage schema fields (Lead Image, Teaser Images,
 * ...) - see academic.types' schema_mixins.py. Stock Volto's ImageWidget
 * can't be reused here: it's built for href-reference image fields (as
 * used in blocks) and both its rendering and its onSelectItem/onSubmitURL
 * handlers assume the field value is a URL (string/array-of-@id), not the
 * {data, encoding, content-type, filename} upload dict (or the
 * {filename, content-type, download, ...} read shape) that a real
 * NamedBlobImage field actually uses - picking an image with it crashes
 * ("e.indexOf is not a function") because it tries string methods on that
 * dict, and even a patched onSelectItem still saves a value shape the
 * backend's NamedBlobImage deserializer rejects ("Invalid image file").
 *
 * This is instead built on the same value shape as the stock FileWidget
 * (which already renders NamedBlobImage values correctly), with one
 * addition: a "pick an existing image" button (object browser) that
 * downloads the chosen image's bytes and re-encodes them into the same
 * upload dict a local file drop already produces.
 */
const CatalogImageWidget = (props) => {
  const { id, value, onChange, isDisabled, fieldSet, openObjectBrowser } =
    props;
  const [fileType, setFileType] = React.useState(false);
  const [picking, setPicking] = React.useState(false);
  const intl = useIntl();
  const location = useLocation();
  const contextUrl = location.pathname;

  const imgAttrs = React.useMemo(() => {
    const data = {};
    if (value?.download) {
      data.item = {
        '@id': value.download.substring(0, value.download.indexOf('/@@images')),
        image: value,
      };
    } else if (value?.data) {
      data.src = `data:${value['content-type']};${value.encoding},${value.data}`;
    }
    return data;
  }, [value]);

  React.useEffect(() => {
    if (value && imageMimetypes.includes(value['content-type'])) {
      setFileType(true);
    }
  }, [value]);

  const applyUploadedFile = (file) => {
    if (!validateFileUploadSize(file, intl.formatMessage)) return;
    readAsDataURL(file).then((data) => {
      const fields = data.match(/^data:(.*);(.*),(.*)$/);
      onChange(id, {
        data: fields[3],
        encoding: fields[2],
        'content-type': fields[1],
        filename: file.name,
      });
    });

    let reader = new FileReader();
    reader.onload = function () {
      const fields = reader.result.match(/^data:(.*);(.*),(.*)$/);
      if (imageMimetypes.includes(fields[1])) {
        setFileType(true);
        let imagePreview = document.getElementById(`field-${id}-image`);
        if (imagePreview) imagePreview.src = reader.result;
      } else {
        setFileType(false);
      }
    };
    reader.readAsDataURL(file);
  };

  const onDrop = (files, rejectedFiles) => {
    rejectedFiles.forEach((file) => {
      file.errors.forEach((err) => {
        if (err.code === 'file-too-large') {
          toast.error(
            <Toast
              error
              title={intl.formatMessage(messages.maxSizeError, {
                size: props.size,
              })}
            />,
          );
        }
        if (err.code === 'file-invalid-type') {
          toast.error(
            <Toast
              error
              title={intl.formatMessage(messages.acceptError, {
                accept: props.accept,
              })}
            />,
          );
        }
      });
    });
    if (files.length < 1) return;
    applyUploadedFile(files[0]);
  };

  const onSelectItem = async (url, { title, image_field }) => {
    setPicking(true);
    try {
      const field = image_field || 'image';
      const downloadUrl = `${flattenToAppURL(url)}/@@images/${field}`;
      const response = await fetch(downloadUrl, { credentials: 'include' });
      if (!response.ok) throw new Error(response.statusText);
      const blob = await response.blob();
      const extension = (blob.type.split('/')[1] || 'jpg').replace(
        'jpeg',
        'jpg',
      );
      applyUploadedFile(
        new File([blob], `${title || 'image'}.${extension}`, {
          type: blob.type,
        }),
      );
    } catch (e) {
      toast.error(
        <Toast
          error
          title={intl.formatMessage(messages.pickImageErrorMessage)}
        />,
      );
    } finally {
      setPicking(false);
    }
  };

  const statusTextA11y = [
    value ? messages.editFile : messages.fileDrag,
    props.required && messages.requiredField,
  ]
    .filter(Boolean)
    .map((m) => intl.formatMessage(m))
    .concat(value?.filename ? [value.filename] : [])
    .join('. ');

  const errorTextA11y = props.error?.length ? props.error.join('. ') : null;

  return (
    <FormFieldWrapper {...props}>
      <Dropzone
        onDrop={onDrop}
        {...(props.size ? { maxSize: props.size } : {})}
        {...(props.accept ? { accept: props.accept } : {})}
      >
        {({ getRootProps, getInputProps, isDragActive }) => (
          <div
            className="file-widget-dropzone"
            role="button"
            aria-labelledby={`fieldset-${fieldSet}-field-label-${id}`}
            aria-describedby={`field-${id}-status`}
            {...getRootProps()}
          >
            {(isDragActive || picking) && <Dimmer active></Dimmer>}
            {fileType ? (
              <Image
                className="image-preview small ui image"
                id={`field-${id}-image`}
                {...imgAttrs}
              />
            ) : (
              <div className="dropzone-placeholder">
                {isDragActive ? (
                  <p className="dropzone-text">
                    {intl.formatMessage(messages.releaseDrag)}
                  </p>
                ) : value ? (
                  <p className="dropzone-text">
                    {intl.formatMessage(messages.editFile)}
                  </p>
                ) : (
                  <p className="dropzone-text">
                    {intl.formatMessage(messages.fileDrag)}
                  </p>
                )}
              </div>
            )}

            <label
              className="label-file-widget-input"
              htmlFor={`field-${id}`}
              aria-hidden="true"
              onClick={(e) => e.stopPropagation()}
            >
              {value
                ? intl.formatMessage(messages.replaceFile)
                : intl.formatMessage(messages.addNewFile)}
            </label>
            <button
              type="button"
              className="label-file-widget-input pick-image-button"
              aria-label={intl.formatMessage(messages.pickAnImage)}
              disabled={isDisabled}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                openObjectBrowser({
                  mode: 'image',
                  onSelectItem,
                  currentPath: contextUrl,
                });
              }}
            >
              <Icon name={navTreeSVG} size="18px" />
            </button>
            <span id={`field-${id}-status`} className="visually-hidden">
              {statusTextA11y}
            </span>
            {errorTextA11y && (
              <span role="alert" className="visually-hidden">
                {errorTextA11y}
              </span>
            )}
            <input
              {...getInputProps({
                type: 'file',
                style: { display: 'none' },
              })}
              id={`field-${id}`}
              {...(props.required && { 'aria-required': true })}
              {...(props.error?.length > 0 && { 'aria-invalid': true })}
              name={id}
              disabled={isDisabled}
            />
          </div>
        )}
      </Dropzone>
      {value && (
        <div className="field-file-name">
          <UniversalLink
            href={value.download}
            aria-label={intl.formatMessage(messages.downloadFile, {
              filename: value.filename,
            })}
            download={true}
          >
            {value.filename}
          </UniversalLink>
          <Button
            type="button"
            icon
            basic
            className="delete-button"
            aria-label="delete file"
            disabled={isDisabled}
            onClick={() => {
              onChange(id, null);
              setFileType(false);
            }}
          >
            <Icon name={deleteSVG} size="20px" />
          </Button>
        </div>
      )}
    </FormFieldWrapper>
  );
};

CatalogImageWidget.propTypes = {
  id: PropTypes.string.isRequired,
  title: PropTypes.string,
  description: PropTypes.string,
  required: PropTypes.bool,
  fieldSet: PropTypes.string,
  error: PropTypes.arrayOf(PropTypes.string),
  value: PropTypes.shape({
    filename: PropTypes.string,
    download: PropTypes.string,
    data: PropTypes.string,
  }),
  onChange: PropTypes.func.isRequired,
  openObjectBrowser: PropTypes.func.isRequired,
};

CatalogImageWidget.defaultProps = {
  description: null,
  required: false,
  fieldSet: null,
  error: [],
  value: null,
};

export default withObjectBrowser(CatalogImageWidget);
